import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Badge, Spinner, Alert, Nav, Tab
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import NouvelInventaire from "./NouvelInventaire";
import InventaireEnCours from "./InventaireEnCours";
import HistoriqueInventaires from "./HistoriqueInventaires";

export default function Inventaires() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('nouveau');
    
    // État pour forcer le rechargement des inventaires en cours
    const [refreshEnCours, setRefreshEnCours] = useState(0);
    const [enCoursCount, setEnCoursCount] = useState(0);

    useEffect(() => {
        fetchStats();
        fetchEnCoursCount();
    }, []);

    // Recharger le compteur quand refreshEnCours change
    useEffect(() => {
        if (refreshEnCours > 0) {
            fetchEnCoursCount();
        }
    }, [refreshEnCours]);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/inventaires.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setStats(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchEnCoursCount = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/inventaires.php?statut=brouillon',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setEnCoursCount(result.data?.length || 0);
            }
        } catch (err) {
            console.error("Erreur comptage", err);
        }
    };

    const handleInventaireCreated = () => {
        // Incrémenter pour forcer le rechargement des données
        setRefreshEnCours(prev => prev + 1);
        // Basculer vers l'onglet "En cours"
        setActiveTab('cours');
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des inventaires...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchStats}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-clipboard-check-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Inventaires
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Gérez vos inventaires et ajustements de stock
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/stock/etat/instantane')}
                                    className="px-4 py-2 d-flex align-items-center gap-2"
                                >
                                    <i className="ri-arrow-left-line"></i>
                                    <span>Retour</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-stack-line text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Inventaires ce mois</small>
                                                <h4 className="mb-0 fw-bold text-primary">{stats.mois?.nb_inventaires || 0}</h4>
                                                <small className="text-muted">{stats.mois?.nb_valides || 0} validés</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-arrow-up-line text-success fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Surplus</small>
                                                <h4 className="mb-0 fw-bold text-success">{stats.ecarts?.surplus || 0}</h4>
                                                <small className="text-muted">articles en trop</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} lg={4}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-arrow-down-line text-danger fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Manquants</small>
                                                <h4 className="mb-0 fw-bold text-danger">{stats.ecarts?.manquant || 0}</h4>
                                                <small className="text-muted">articles manquants</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Onglets */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                <Nav.Item>
                                    <Nav.Link eventKey="nouveau">
                                        <i className="ri-add-circle-line me-2"></i>
                                        Nouvel inventaire
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="cours">
                                        <i className="ri-time-line me-2"></i>
                                        En cours
                                        {enCoursCount > 0 && (
                                            <Badge bg="warning" className="ms-2 rounded-pill">{enCoursCount}</Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="historique">
                                        <i className="ri-history-line me-2"></i>
                                        Historique
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body>
                            <Tab.Content>
                                <Tab.Pane active={activeTab === 'nouveau'}>
                                    <NouvelInventaire onInventaireCreated={handleInventaireCreated} />
                                </Tab.Pane>
                                <Tab.Pane active={activeTab === 'cours'}>
                                    <InventaireEnCours key={refreshEnCours} />
                                </Tab.Pane>
                                <Tab.Pane active={activeTab === 'historique'}>
                                    <HistoriqueInventaires />
                                </Tab.Pane>
                            </Tab.Content>
                        </Card.Body>
                    </Card>
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .nav-tabs .nav-link {
                        color: #64748b;
                        border: none;
                        padding: 0.75rem 1.5rem;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                    }

                    .nav-tabs .nav-link.active {
                        color: #4361ee;
                        background: none;
                        border-bottom: 2px solid #4361ee;
                    }

                    .nav-tabs .nav-link:hover {
                        color: #4361ee;
                        background: rgba(67, 97, 238, 0.05);
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}